import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Medal } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import { MESES } from '@/components/constants/meses';
import { GLASS_CARD } from '@/components/ui/glass';
import DashboardSemana from './DashboardSemana';
import DashboardHorasPico from './DashboardHorasPico';

interface ServicioMes {
  servicio: string;
  total_citas: number;
  total_ingresos: number | null;
}

interface Props {
  empresaId: string;
  serviciosMes: ServicioMes[];
  mes: number;
  anio: number;
}

type Orden = 'citas' | 'ingresos';

const TOP = 5;
const RANK_COLORS = ['#2170b8', '#22c55e', '#f59e0b', '#8b5cf6', '#ef4444'];

const euro = (v: number) =>
  `${v.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €`;

const CARD = `relative isolate overflow-hidden rounded-2xl ${GLASS_CARD} transition-shadow duration-300 hover:shadow-[0_12px_32px_-12px_rgba(16,50,84,0.35)] dark:hover:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)]`;

const Brillo = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/15"
  />
);

const DashboardCharts = ({ empresaId, serviciosMes, mes, anio }: Props) => {
  const { t } = useTranslation();
  const [orden, setOrden] = useState<Orden>('citas');

  const servicios = useMemo(() => {
    const base = (serviciosMes ?? [])
      .map((s) => ({
        servicio: s.servicio,
        citas: Number(s.total_citas ?? 0),
        ingresos: Number(s.total_ingresos ?? 0),
      }))
      .filter((s) => s.citas > 0 || s.ingresos > 0)
      .sort((a, b) => b[orden] - a[orden]);

    const totalCitas = base.reduce((a, s) => a + s.citas, 0);
    const totalIngresos = base.reduce((a, s) => a + s.ingresos, 0);
    const top = base.slice(0, TOP);
    const resto = base.slice(TOP);
    const otros = resto.length
      ? {
          servicio: t('dashboardCharts.others', { count: resto.length }),
          citas: resto.reduce((a, s) => a + s.citas, 0),
          ingresos: resto.reduce((a, s) => a + s.ingresos, 0),
        }
      : null;

    const total = orden === 'citas' ? totalCitas : totalIngresos;
    const max = Math.max(1, ...top.map((s) => s[orden]));

    return { top, otros, totalCitas, totalIngresos, total, max };
  }, [serviciosMes, orden, t]);

  const nombreMes = MESES[mes - 1]
    ? MESES[mes - 1].charAt(0).toUpperCase() + MESES[mes - 1].slice(1)
    : '';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <DashboardSemana empresaId={empresaId} />

      <Card className={`${CARD} tour-chart-servicios`}>
        <Brillo />
        <CardContent className="p-4 sm:p-6 flex flex-col h-full">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-[0.06em]">
                {t('dashboardCharts.topServices')}
              </h2>
              <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
                <span className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 dark:text-white tabular-nums">
                  {orden === 'citas'
                    ? servicios.totalCitas
                    : euro(servicios.totalIngresos)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {orden === 'citas' ? t('dashboardCharts.appointments') : ''} ·{' '}
                  {nombreMes}
                </span>
              </div>
            </div>

            <div className="relative flex shrink-0 rounded-xl bg-gray-100/80 dark:bg-white/5 p-1">
              {(['citas', 'ingresos'] as Orden[]).map((o) => (
                <button
                  key={o}
                  onClick={() => setOrden(o)}
                  className={`relative z-10 px-2.5 py-1 text-[11px] font-semibold rounded-lg transition-colors ${
                    orden === o
                      ? 'text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {orden === o && (
                    <motion.span
                      layoutId="orden-servicios"
                      className="absolute inset-0 -z-10 rounded-lg bg-[#103254] dark:bg-[#2170b8] shadow"
                      transition={{ type: 'spring', stiffness: 400, damping: 32 }}
                    />
                  )}
                  {o === 'citas'
                    ? t('dashboardCharts.byAppointments')
                    : t('dashboardCharts.income')}
                </button>
              ))}
            </div>
          </div>

          {servicios.top.length === 0 ? (
            <div className="flex-1 min-h-[12rem] flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">
              {t('dashboardCharts.noServices')}
            </div>
          ) : (
            <ul className="mt-4 space-y-3">
              {servicios.top.map((s, i) => {
                const valor = s[orden];
                const pct = servicios.total > 0 ? (valor / servicios.total) * 100 : 0;
                const ticket = s.citas > 0 ? s.ingresos / s.citas : 0;
                const color = RANK_COLORS[i % RANK_COLORS.length];
                return (
                  <motion.li
                    key={s.servicio}
                    layout
                    transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                    className="group"
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="w-6 h-6 shrink-0 rounded-lg flex items-center justify-center text-[11px] font-bold text-white"
                        style={{ background: color }}
                      >
                        {i === 0 ? <Medal className="w-3.5 h-3.5" /> : i + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline justify-between gap-2">
                          <span
                            title={s.servicio}
                            className="truncate text-sm font-medium text-gray-800 dark:text-gray-100"
                          >
                            {s.servicio}
                          </span>
                          <span className="shrink-0 text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                            {orden === 'citas' ? s.citas : euro(s.ingresos)}
                          </span>
                        </div>
                        <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-white/5 overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ background: color }}
                            initial={{ width: 0 }}
                            animate={{ width: `${(valor / servicios.max) * 100}%` }}
                            transition={{ duration: 0.6, ease: 'easeOut' }}
                          />
                        </div>
                        <div className="mt-1 flex justify-between text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
                          <span>
                            {pct.toFixed(0)}% {t('dashboardCharts.ofTotal')}
                          </span>
                          <span>
                            {orden === 'citas'
                              ? euro(s.ingresos)
                              : `${s.citas} ${t('dashboardCharts.appointments')}`}
                            {ticket > 0 &&
                              ` · ${t('dashboardCharts.avgTicket')} ${euro(ticket)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                );
              })}

              {servicios.otros && (
                <li className="flex items-center justify-between gap-2 pt-2 border-t border-gray-100 dark:border-white/5 text-xs text-gray-500 dark:text-gray-400">
                  <span>{servicios.otros.servicio}</span>
                  <span className="font-semibold tabular-nums">
                    {orden === 'citas'
                      ? servicios.otros.citas
                      : euro(servicios.otros.ingresos)}
                  </span>
                </li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      <DashboardHorasPico empresaId={empresaId} mes={mes} anio={anio} filtrarTrabajadores />
    </div>
  );
};

export default DashboardCharts;
